import { useInviteSettings, useInviteUser } from "@vidario/api-client";
import { formatCount, HOUR_NOUN, nounFor } from "@vidario/domain";
import { Button, Input, Modal, Select, useToast } from "@vidario/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, Mail } from "lucide-react";
import { type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  firstName: z.string().trim().min(1, "Bitte den Vornamen angeben."),
  lastName: z.string().trim().min(1, "Bitte den Nachnamen angeben."),
  email: z.string().email("Bitte eine gültige E-Mail-Adresse eingeben."),
  role: z.enum(["recruiter", "admin"]),
  company: z.string().trim().max(200).optional(),
});
type InviteForm = z.infer<typeof schema>;

export interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Admin-only "Neuen Nutzer anlegen" (MVP §3.2). Creates the user (with
 * an unusable password) and sends an invitation email with a set-password
 * link. Idempotent: re-invites an existing email instead of erroring. */
export function AddUserDialog({ open, onOpenChange }: AddUserDialogProps): ReactElement {
  const toast = useToast();
  const inviteUser = useInviteUser();
  const inviteSettings = useInviteSettings({ enabled: open });
  const hours = inviteSettings.data?.invitationLinkHours;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<InviteForm>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "", email: "", role: "recruiter", company: "" },
  });

  const role = watch("role");

  const submit = async (values: InviteForm): Promise<void> => {
    try {
      const result = await inviteUser.mutateAsync({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
        company: values.company?.trim() || null,
      });
      toast.push({
        kind: "success",
        title: result.existing ? "Einladung erneut versendet" : "Nutzer eingeladen",
        message: result.existing
          ? `Für ${result.user.email} existiert bereits ein Konto — neuer Setup-Link verschickt.`
          : `${result.user.name} bekommt eine E-Mail mit dem Setup-Link.`,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Einladung fehlgeschlagen",
        message: err instanceof Error ? err.message : "Unbekannter Fehler.",
      });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Neuen Nutzer anlegen"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            loading={isSubmitting || inviteUser.isPending}
            onClick={() => void handleSubmit(submit)()}
          >
            Anlegen &amp; Einladung senden
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => void handleSubmit(submit)(e)}
        className="grid grid-cols-2 gap-4"
        noValidate
      >
        <div>
          <Input
            label="Vorname"
            required
            placeholder="z. B. Anna"
            {...register("firstName")}
            {...(errors.firstName?.message && { error: errors.firstName.message })}
          />
        </div>
        <div>
          <Input
            label="Nachname"
            required
            placeholder="z. B. Schmidt"
            {...register("lastName")}
            {...(errors.lastName?.message && { error: errors.lastName.message })}
          />
        </div>
        <div className="col-span-2">
          <Input
            label="E-Mail"
            required
            icon={<Mail size={16} />}
            placeholder="anna@beispiel.de"
            {...register("email")}
            {...(errors.email?.message && { error: errors.email.message })}
          />
        </div>
        <div>
          <Select
            label="Rolle"
            required
            value={role}
            onValueChange={(v) =>
              setValue("role", v as InviteForm["role"], { shouldValidate: true })
            }
            options={[
              { value: "recruiter", label: "Recruiter" },
              { value: "admin", label: "Admin" },
            ]}
          />
        </div>
        <div>
          <Input
            label="Unternehmen"
            placeholder="optional"
            {...register("company")}
            {...(errors.company?.message && { error: errors.company.message })}
          />
        </div>
      </form>
      <div className="mt-4 flex gap-2 rounded-md bg-info-50 p-3 text-xs text-info-700">
        <Info size={14} className="shrink-0" />
        <span>
          Der eingeladene Nutzer bekommt eine E-Mail mit einem Setup-Link für sein Passwort
          {typeof hours === "number" && (
            <>
              {" "}
              ({formatCount(hours)}&nbsp;{nounFor(hours, HOUR_NOUN)} gültig)
            </>
          )}
          .
        </span>
      </div>
    </Modal>
  );
}
